import { useCallback, useEffect, useMemo, useState } from "react";
import { taskService } from "@miplatform/api";
import { ATTEMPTS_HUB_NAME } from "@miplatform/constants";
import {
  findHubByName,
  subscribeToAutoAssignStatusUpdate,
} from "@miplatform/signalr";
import {
  AutoAssignWorkOrderTemplateStatus,
  IAutoAssignWorkOrderTemplateStatus,
  ICalendarWorkTemplateBase,
} from "@miplatform/types";
import { formatDate, getLatest, isDateAfter } from "@miplatform/utils";

type UseAutoAssignWorkOrderModalViewModelArgs = {
  open: boolean;
  calendarSetId?: string;
  workBaseTemplates: ICalendarWorkTemplateBase[];
  isLoading: boolean;
  onClose: () => void;
  onConfirm?: (workOrderTemplateIds: string[]) => void | Promise<void>;
};

export function useAutoAssignWorkOrderModalViewModel({
  open,
  calendarSetId,
  workBaseTemplates,
  isLoading,
  onClose,
  onConfirm,
}: UseAutoAssignWorkOrderModalViewModelArgs) {
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<
    IAutoAssignWorkOrderTemplateStatus[]
  >([]);
  const [isStatusLoading, setIsStatusLoading] = useState(false);

  const statusByTemplateId = useMemo(
    () =>
      new Map(statuses.map((status) => [status.workOrderTemplateId, status])),
    [statuses]
  );

  const assignableTemplateIds = useMemo(
    () =>
      workBaseTemplates
        .filter(
          (template) =>
            !isActiveStatus(statusByTemplateId.get(template.id)?.status)
        )
        .map((template) => template.id),
    [statusByTemplateId, workBaseTemplates]
  );

  const loadStatuses = useCallback(async () => {
    if (!calendarSetId) {
      setStatuses([]);
      return;
    }

    setIsStatusLoading(true);
    try {
      const response = await taskService.workOrders.getAutoAssignStatuses({
        calendarSetId,
      });
      setStatuses(response?.data ?? []);
    } finally {
      setIsStatusLoading(false);
    }
  }, [calendarSetId]);

  const handleClose = () => {
    setSelectedTemplateIds([]);
    onClose();
  };

  const handleToggleTemplate = (templateId: string) => {
    setSelectedTemplateIds((current) =>
      current.includes(templateId)
        ? current.filter((id) => id !== templateId)
        : [...current, templateId]
    );
  };

  const handleSelectAll = () => {
    const allSelected =
      assignableTemplateIds.length > 0 &&
      selectedTemplateIds.length === assignableTemplateIds.length;
    setSelectedTemplateIds(allSelected ? [] : assignableTemplateIds);
  };

  const handleConfirm = async () => {
    await onConfirm?.(selectedTemplateIds);
    await loadStatuses();
  };

  useEffect(() => {
    if (open) {
      loadStatuses();
    } else {
      setSelectedTemplateIds([]);
    }
  }, [loadStatuses, open]);

  useEffect(() => {
    if (!open || !calendarSetId) {
      return undefined;
    }

    const hub = findHubByName(ATTEMPTS_HUB_NAME);
    if (!hub) {
      return undefined;
    }

    const subscription = subscribeToAutoAssignStatusUpdate(
      hub,
      (updates: IAutoAssignWorkOrderTemplateStatus[]) => {
        setStatuses((current) => mergeStatusUpdates(current, updates));
      },
      (status: IAutoAssignWorkOrderTemplateStatus) =>
        status?.calendarSetId === calendarSetId
    );

    return () => subscription.unsubscribe();
  }, [calendarSetId, open]);

  const hasActiveStatus = statuses.some(({ status }) => isActiveStatus(status));

  useEffect(() => {
    if (!open || !hasActiveStatus) {
      return undefined;
    }

    const interval = window.setInterval(loadStatuses, 2000);
    return () => window.clearInterval(interval);
  }, [hasActiveStatus, loadStatuses, open]);

  const latestOperationStatuses = useMemo(() => {
    const active = statuses.filter(({ status }) => isActiveStatus(status));
    const source = active.length ? active : statuses;
    const latest = getLatest(source, (status) => status.startedOn);

    return latest
      ? statuses.filter((status) => status.operationId === latest.operationId)
      : [];
  }, [statuses]);

  const progress = useMemo(() => {
    const total = sumStatusCount(latestOperationStatuses, "totalWorkOrders");
    const processed = sumStatusCount(
      latestOperationStatuses,
      "processedWorkOrders"
    );

    return {
      total,
      processed,
      assigned: sumStatusCount(latestOperationStatuses, "assignedWorkOrders"),
      failed: sumStatusCount(latestOperationStatuses, "failedWorkOrders"),
      percentage: total === 0 ? 100 : Math.round((processed / total) * 100),
    };
  }, [latestOperationStatuses]);

  const dayProgress = useMemo(() => {
    const total = maxStatusCount(
      latestOperationStatuses,
      "numberOfDaysToProcess"
    );
    const current = Math.min(
      maxStatusCount(latestOperationStatuses, "currentDayToProcess"),
      total
    );

    return {
      total,
      current,
      percentage: total === 0 ? 0 : Math.round((current / total) * 100),
    };
  }, [latestOperationStatuses]);

  const isUnassigningWorkOrders = latestOperationStatuses.some(
    (status) => status.isUnassigningWorkOrders
  );

  const latestOperationFailed = latestOperationStatuses.some(
    ({ status }) => status === AutoAssignWorkOrderTemplateStatus.Failed
  );
  const latestOperationQueued =
    latestOperationStatuses.every(
      ({ status }) => status === AutoAssignWorkOrderTemplateStatus.Queued
    ) && latestOperationStatuses.length > 0;
  const latestOperationInProgress = latestOperationStatuses.some(
    ({ status }) => status === AutoAssignWorkOrderTemplateStatus.InProgress
  );

  const getLatestOperationStatus = () => {
    return latestOperationStatuses.sort((a, b) =>
      isDateAfter(b.startedOn, a.startedOn) ? 1 : -1
    ).length > 0
      ? latestOperationStatuses[0]
      : undefined;
  };

  return {
    selectedTemplateIds,
    progress,
    dayProgress,
    isUnassigningWorkOrders,
    latestOperationQueued,
    getLatestOperationStatus,
    hasLatestOperation: latestOperationStatuses.length > 0,
    progressTitle: latestOperationInProgress
      ? "Auto-assignment is in progress"
      : latestOperationQueued
      ? "Auto-assignment run was queued"
      : latestOperationFailed
      ? "Latest auto-assignment failed"
      : "Latest auto-assignment completed",
    isModalLoading: isLoading || (isStatusLoading && statuses.length === 0),
    isConfirmDisabled:
      isLoading || latestOperationQueued || latestOperationInProgress,
    handleClose,
    handleConfirm,
    handleSelectAll,
    handleToggleTemplate,
    isTemplateActive: (templateId: string) =>
      isActiveStatus(statusByTemplateId.get(templateId)?.status),
    getTemplateStatusText: (templateId: string) =>
      getStatusText(statusByTemplateId.get(templateId)),
  };
}

function mergeStatusUpdates(
  current: IAutoAssignWorkOrderTemplateStatus[],
  updates: IAutoAssignWorkOrderTemplateStatus[]
) {
  const byTemplateId = new Map(
    current.map((status) => [status.workOrderTemplateId, status])
  );

  for (const update of updates) {
    const currentStatus = byTemplateId.get(update.workOrderTemplateId);
    const isOlderOperation =
      currentStatus && isDateAfter(currentStatus.startedOn, update.startedOn);
    const isOlderProgress =
      currentStatus?.operationId === update.operationId &&
      currentStatus.processedWorkOrders > update.processedWorkOrders;

    if (!isOlderOperation && !isOlderProgress) {
      byTemplateId.set(update.workOrderTemplateId, update);
    }
  }

  return [...byTemplateId.values()];
}

function sumStatusCount(
  statuses: IAutoAssignWorkOrderTemplateStatus[],
  field:
    | "totalWorkOrders"
    | "processedWorkOrders"
    | "assignedWorkOrders"
    | "failedWorkOrders"
) {
  return statuses.reduce((sum, status) => sum + status[field], 0);
}

function maxStatusCount(
  statuses: IAutoAssignWorkOrderTemplateStatus[],
  field: "numberOfDaysToProcess" | "currentDayToProcess"
) {
  return statuses.reduce((max, status) => Math.max(max, status[field] ?? 0), 0);
}

function getStatusText(status?: IAutoAssignWorkOrderTemplateStatus) {
  if (!status) {
    return "No auto-assignment status yet";
  }

  const date = formatDate(
    status.completedOn ?? status.startedOn,
    "MMM D, YYYY h:mm A"
  );

  switch (status.status) {
    case AutoAssignWorkOrderTemplateStatus.Queued:
      return `Execution will start in the future · ${status.totalWorkOrders} work orders`;
    case AutoAssignWorkOrderTemplateStatus.InProgress:
      return status.isUnassigningWorkOrders
        ? "Unassigning current work orders…"
        : `In progress · ${status.processedWorkOrders} of ${status.totalWorkOrders}`;
    case AutoAssignWorkOrderTemplateStatus.Failed:
      return `Processing failed ${date} · ${status.processedWorkOrders} of ${status.totalWorkOrders} processed`;
    case AutoAssignWorkOrderTemplateStatus.Completed:
    default:
      return `Completed ${date} · ${status.assignedWorkOrders} assigned, ${status.failedWorkOrders} not assigned`;
  }
}

function isActiveStatus(status?: AutoAssignWorkOrderTemplateStatus) {
  return (
    status === AutoAssignWorkOrderTemplateStatus.Queued ||
    status === AutoAssignWorkOrderTemplateStatus.InProgress
  );
}
